// Footer.jsx
import React, { useEffect, useState } from "react";
import { Link } from 'react-scroll';
import { FiMail } from 'react-icons/fi';
import { FaTwitter, FaInstagram } from 'react-icons/fa';

import "../styles/footer.css";

const TERMS_HTML_URL = "/legal/terms.html";   // <-- serve HTML from /public

export default function Footer() {
  const [showModal, setShowModal] = useState(false);
  const [hasAccepted, setHasAccepted] = useState(false);
  const [checked, setChecked] = useState(false);
  const [termsHtml, setTermsHtml] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [pendingRoute, setPendingRoute] = useState(null); // route to go to after acceptance

  // Expose 5 custom gated-nav "slots" you can call from anywhere, e.g. onClick={() => window.gateNav1('/services')}
  useEffect(() => {
    const gateNav = (route) => {
      if (!hasAccepted) {
        setPendingRoute(route);
        openTerms();
      } else {
        window.location.href = route;
      }
    };

    window.gateNav1 = (route) => gateNav(route);
    window.gateNav2 = (route) => gateNav(route);
    window.gateNav3 = (route) => gateNav(route);
    window.gateNav4 = (route) => gateNav(route);
    window.gateNav5 = (route) => gateNav(route);

    return () => {
      delete window.gateNav1;
      delete window.gateNav2;
      delete window.gateNav3;
      delete window.gateNav4;
      delete window.gateNav5;
    };
  }, [hasAccepted]); // re-bind if acceptance changes

  const openTerms = async () => {
    setShowModal(true);
    setChecked(false);
    setErr("");
    setLoading(true);
    try {
      const res = await fetch(TERMS_HTML_URL, { cache: "no-store" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const html = await res.text();
      setTermsHtml(html);
    } catch (e) {
      setErr("Couldn’t load Terms. Please try again or use the download link.");
    } finally {
      setLoading(false);
      document.body.classList.add("modal-open");
    }
  };

  const closeTerms = () => {
    setShowModal(false);
    document.body.classList.remove("modal-open");
  };

  const acceptTerms = () => {
    // Session-only acceptance (resets on reload)
    setHasAccepted(true);
    closeTerms();
    if (pendingRoute) {
      const target = pendingRoute;
      setPendingRoute(null);
      window.location.href = target;
    }
  };

  return (
    <div className="footer-container">
      <p className="footer-text">
        <span className="footer-item">
          <FiMail size={24} />
          <span className="footer-item-text">l.mcguire@galaxyedgesafety.com</span>
        </span>

        <span className="footer-item">
          <FaInstagram size={24} />
          <span className="footer-item-text">@galaxyedgesafety</span>
        </span>

        <span className="footer-item">
          <FaTwitter size={24} />
          <span className="footer-item-text">@galaxyedgesafety</span>
        </span>

        <span className="footer-item">
          <button className="footer-link-button" type="button" onClick={openTerms}>
            <svg
              className="footer-link-icon"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              aria-hidden="true"
              focusable="false"
            >
              <rect x="3" y="7" width="18" height="12" rx="3" ry="3" fill="none" stroke="currentColor" strokeWidth="1.5"/>
              <rect x="6.5" y="10" width="11" height="2.6" rx="1.3" fill="currentColor"/>
              <circle cx="18.5" cy="13" r="0.9" fill="currentColor"/>
            </svg>
            <span>Terms &amp; Conditions</span>
          </button>

          {hasAccepted && <span className="footer-badge">✓ Accepted</span>}
        </span>

        <span className="footer-item">
          <span className="footer-item-copyright">
            Copyright © 2024 Galaxy Edge Safety - All Rights Reserved.
          </span>
        </span>
      </p>

      {/* Modal */}
      {showModal && (
        <div
          id="terms-modal"
          role="dialog"
          aria-modal="true"
          aria-label="Terms and Conditions"
          className="terms-overlay"
          onClick={(e) => {
            if (e.target.classList.contains("terms-overlay")) closeTerms();
          }}
        >
          <div className="terms-modal">
            <div className="terms-header">
              <h3>Terms &amp; Conditions</h3>
              <button className="terms-close" aria-label="Close" onClick={closeTerms}>×</button>
            </div>

            <div className="terms-body">
              {loading && (
                <div className="terms-loading">Loading…</div>
              )}

              {!loading && err && (
                <div className="terms-error">
                  {err} &nbsp;
                  <a href="/legal/terms-2025-09-24.pdf" target="_blank" rel="noreferrer">
                    Download PDF
                  </a>
                </div>
              )}

              {!loading && !err && (
                <article
                  className="terms-html"
                  dangerouslySetInnerHTML={{ __html: termsHtml }}
                />
              )}
            </div>

            <div className="terms-actions">
              <label className="terms-check">
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={(e) => setChecked(e.target.checked)}
                />
                <span>I have read and accept the Terms &amp; Conditions.</span>
              </label>

              <div className="terms-buttons">
                <button className="btn-secondary" onClick={closeTerms}>Cancel</button>
                <button className="btn-primary" onClick={acceptTerms} disabled={!checked}>
                  Accept
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
